/**
 * @fileoverview Indexed local-only ledger for synced documents changed on this device.
 * @module local-db/services/localDirtyLedger
 *
 * Responsibilities:
 * - Record local document writes for offline queue visibility.
 * - Remove acknowledged ledger entries and clean up missing domain documents.
 * - Count visible dirty documents without scanning domain tables.
 *
 * Dependencies:
 * - Dexie database and table APIs.
 *
 * Architecture:
 * - Domain tables remain authoritative; this compound-key table is only an auxiliary index.
 * - Operations safely no-op when an older or non-mobile database has no ledger table.
 *
 * Notes:
 * - Acknowledgement and local-write ISO timestamps are compared lexically.
 * - The write helper must be called inside the caller's domain-table transaction.
 */

import type Dexie from 'dexie';

/**
 * @contract LocalDirtyDoc
 * Purpose: Represent one locally modified synced document awaiting push reconciliation.
 * Fields: Normalized collection key, document id, and last local updatedAt timestamp.
 * Rules: The compound collection/document key exists only in mobile IndexedDB.
 * Sync: Cleared by a covering server acknowledgement; missing domain records are pruned.
 */
export interface LocalDirtyDoc {
  collection: string;
  docId: string;
  updatedAt: string;
}

/**
 * @contract LocalDirtyLedgerDatabase
 * Purpose: Describe the Dexie table access required by dirty-ledger operations.
 * Fields: Table-name metadata and a table accessor.
 * Rules: The table accessor must return native Dexie tables.
 * Sync: Supports both raw Dexie and the mobile table facade.
 */
export interface LocalDirtyLedgerDatabase {
  tables: Array<{ name: string }>;
  table(name: string): Dexie.Table;
}

function dirtyTable(db: LocalDirtyLedgerDatabase | null | undefined): Dexie.Table | null {
  if (!db) return null;
  try {
    return db.tables.some((table) => table.name === 'localDirtyDocs')
      ? db.table('localDirtyDocs')
      : null;
  } catch {
    return null;
  }
}

/**
 * @process markLocalDocDirty
 * Steps: Upsert the normalized collection/document key with its latest local timestamp.
 * Inputs: Raw Dexie instance, collection key, document id, and updatedAt.
 * Outputs: Resolves after the ledger row is written or when the table is unavailable.
 * Failure Handling: Dexie write errors propagate so the caller's domain transaction rolls back.
 * Idempotency: REQUIRED.
 *
 * @localFirst
 * Source of Truth: The domain document remains authoritative; this is a local queue index.
 * Write Flow: Put one compound-key ledger row in the caller's read-write transaction.
 * Sync Strategy: Local-only; replication uses it for visibility and cleanup, never push selection.
 * Conflict Resolution: A later write replaces the timestamp for the same document.
 *
 * @errorHandling
 * Strategy: Propagate Dexie failures so the caller can roll back its domain transaction.
 * User Impact: Failed writes do not appear in the queued-document count.
 * Logging: REQUIRED at the user-facing or replication call site.
 */
export async function markLocalDocDirty(
  db: LocalDirtyLedgerDatabase | null | undefined,
  collection: string,
  docId: string,
  updatedAt: string,
): Promise<void> {
  const table = dirtyTable(db);
  if (
    !table ||
    !collection ||
    collection === 'audit_logs' ||
    collection === 'auditLogs' ||
    !docId ||
    !updatedAt
  ) {
    return;
  }
  await table.put({ collection, docId, updatedAt } satisfies LocalDirtyDoc);
}

/**
 * @process clearAcknowledgedDirtyDocs
 * Steps: Delete each ledger row only when its acknowledgement timestamp covers the stored local write.
 * Inputs: Raw Dexie instance, normalized collection, and acknowledged document ids/timestamps.
 * Outputs: Resolves after eligible ledger rows are deleted.
 * Failure Handling: Dexie errors propagate; replication callers log and swallow cleanup failures.
 * Idempotency: REQUIRED.
 *
 * @localFirst
 * Source of Truth: The ledger timestamp tracks the latest local edit for each document.
 * Write Flow: Delete acknowledged compound keys in a Dexie transaction.
 * Sync Strategy: A stale acknowledgement cannot clear a newer offline edit.
 * Conflict Resolution: Acknowledgements older than the local timestamp leave the row intact.
 *
 * @errorHandling
 * Strategy: Propagate storage failures to the replication boundary for best-effort logging.
 * User Impact: Failed cleanup can temporarily overstate the offline queue, but never blocks push.
 * Logging: REQUIRED at the replication call site.
 */
export async function clearAcknowledgedDirtyDocs(
  db: LocalDirtyLedgerDatabase | null | undefined,
  collection: string,
  acked: Array<{ _id: string; updatedAt?: string }>,
): Promise<void> {
  const table = dirtyTable(db);
  if (!table || !collection || acked.length === 0) return;

  await table.db.transaction('rw', table, async () => {
    for (const doc of acked) {
      if (!doc?._id || typeof doc.updatedAt !== 'string') continue;
      const docId = String(doc._id);
      const row = await table.get([collection, docId]) as LocalDirtyDoc | undefined;
      if (row && doc.updatedAt >= row.updatedAt) {
        await table.delete([collection, docId]);
      }
    }
  });
}

/**
 * @process reconcileDirtyLedger
 * Steps: Remove ledger rows for domain documents that no longer exist; preserve existing records until acknowledged.
 * Inputs: Raw Dexie instance and normalized collection.
 * Outputs: Resolves after missing-document rows are removed.
 * Failure Handling: Dexie errors propagate; replication callers log and swallow cleanup failures.
 * Idempotency: REQUIRED.
 *
 * @localFirst
 * Source of Truth: Domain documents and explicit server acknowledgements determine ledger state.
 * Write Flow: Read documents by primary key and delete only rows for missing documents.
 * Sync Strategy: Reconciliation runs after a collection push cycle; push selection is unchanged.
 * Conflict Resolution: Existing records, including tombstones, remain dirty until a covering acknowledgement.
 *
 * @errorHandling
 * Strategy: Propagate storage failures to the replication boundary for best-effort logging.
 * User Impact: Failed cleanup can temporarily overstate the offline queue, but never blocks push.
 * Logging: REQUIRED at the replication call site.
 */
export async function reconcileDirtyLedger(
  db: LocalDirtyLedgerDatabase | null | undefined,
  collection: string,
): Promise<void> {
  const ledger = dirtyTable(db);
  if (!ledger || !db || !collection) return;

  let source: Dexie.Table;
  try {
    if (!db.tables.some((candidate) => candidate.name === collection)) return;
    source = db.table(collection);
  } catch {
    return;
  }

  await ledger.db.transaction('rw', ledger, source, async () => {
    const rows = await ledger.where('collection').equals(collection).toArray() as LocalDirtyDoc[];
    for (const row of rows) {
      const document = await source.get(row.docId);
      if (!document) {
        await ledger.delete([row.collection, row.docId]);
        continue;
      }
    }
  });
}

/**
 * @process countUserVisibleDirtyDocs
 * Steps: Count ledger rows through the collection index while excluding audit records.
 * Inputs: Raw Dexie instance.
 * Outputs: Number of user-visible dirty documents, or zero when the ledger is absent.
 * Failure Handling: Missing ledger returns zero; query failures propagate.
 * Idempotency: REQUIRED.
 *
 * @localFirst
 * Source of Truth: The local dirty-document ledger.
 * Write Flow: None; this function performs an indexed count only.
 * Sync Strategy: No sync work is started or modified.
 * Conflict Resolution: Not applicable.
 *
 * @errorHandling
 * Strategy: Return zero when the ledger table is absent and propagate query failures.
 * User Impact: A failed query is handled by the liveQuery consumer; no domain data changes.
 * Logging: The hook's liveQuery boundary handles query errors.
 */
export async function countUserVisibleDirtyDocs(
  db: LocalDirtyLedgerDatabase | null | undefined,
): Promise<number> {
  const table = dirtyTable(db);
  if (!table) return 0;
  return table.where('collection').notEqual('audit_logs').count();
}
